import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "../contexts/TranslationContext";
import ruJson from "../contexts/ru.json";
import { deleteUser, updateUser } from "../api/apiServiceFetch";
import ConfirmPopup from "./ConfirmPopup";
import "./Popup.scss";

const CUSTOM_OPTION = "__custom__";

const WORLDVIEW_OPTIONS = [
	{ value: "iron", labelKey: "worldview_iron", aliases: ["iron", "worldview_iron"] },
	{
		value: "orthodox",
		labelKey: "worldview_orthodox",
		aliases: ["orthodox", "worldview_orthodox"],
	},
	{ value: "islam", labelKey: "worldview_islam", aliases: ["islam", "worldview_islam"] },
	{
		value: "dont_tell",
		labelKey: "dont_tell",
		aliases: ["dont_tell"],
	},
];

const GOAL_OPTIONS = [
	{ value: "family", labelKey: "goal_family", aliases: ["family", "goal_family"] },
	{ value: "serious", labelKey: "goal_serious", aliases: ["serious", "goal_serious"] },
	{ value: "friends", labelKey: "goal_friends", aliases: ["friends", "goal_friends"] },
	{ value: "business", labelKey: "goal_business", aliases: ["business", "goal_business"] },
];

const CHILDREN_OPTIONS = [
	{ value: "none", labelKey: "children_none", aliases: ["none", "children_none"] },
	{
		value: "none_but_want",
		labelKey: "children_none_but_want",
		aliases: ["none_but_want", "children_none_but_want"],
	},
	{ value: "have", labelKey: "children_have", aliases: ["have", "children_have"] },
	{
		value: "have_more",
		labelKey: "children_have_more",
		aliases: ["have_more", "children_have_more"],
	},
];

const LANGUAGE_OPTIONS = [
	{ value: "os_iron", labelKey: "os_iron" },
	{ value: "os_digor", labelKey: "os_digor" },
	{ value: "ru", labelKey: "ru" },
	{ value: "en", labelKey: "en" },
];

const normalizeMappedValue = (value, options) => {
	const raw = String(value ?? "").trim();
	if (!raw) return "";

	const lowered = raw.toLowerCase();
	const found = options.find((option) =>
		Array.isArray(option.aliases)
			? option.aliases.some((alias) => String(alias).toLowerCase() === lowered)
			: String(option.value).toLowerCase() === lowered,
	);

	return found ? found.value : raw;
};

const parseIntSafe = (value, fallback = 0) => {
	const parsed = Number.parseInt(String(value ?? ""), 10);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDate = (date) => {
	if (!date) return "";

	try {
		return format(new Date(date), "yyyy-MM-dd");
	} catch {
		return "";
	}
};

const isKnownValue = (value, options) =>
	options.some((option) => String(option.value) === String(value));

const mapProfessionCodes = (items) =>
	(items || [])
		.filter((item) => String(item?.code || "").startsWith("act_"))
		.map((item) => String(item.code));

const Popup = ({
	isOpen,
	onClose,
	editData,
	userDetails,
	onConfirm,
	onSaved,
}) => {
	const { t } = useTranslation();
	const [showPopup, setShowPopup] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const professionCodes = useMemo(() => mapProfessionCodes(ruJson), []);

	const [userData, setUserData] = useState({
		userId: 0,
		name: "",
		surName: "",
		gender: 1,
		birthday: "",
		worldview: "",
		description: "",
		profession: "",
		goal: "",
		familyNames: "",
		children: "",
		country: "",
		city: "",
		freezeState: false,
		incoState: false,
		minAge: 18,
		maxAge: 80,
		height: 170,
		languages: "",
	});

	React.useEffect(() => {
		if (!isOpen) return;

		setUserData({
			userId: Number(editData?.userId ?? editData?.id ?? 0),
			name: editData?.name ?? "",
			surName: editData?.surName ?? editData?.sName ?? "",
			gender: Number(editData?.gender ?? editData?.sex ?? 1),
			birthday: formatDate(editData?.birthday),
			worldview: normalizeMappedValue(userDetails?.worldview, WORLDVIEW_OPTIONS),
			description: userDetails?.description ?? editData?.description ?? "",
			profession: userDetails?.profession ?? editData?.profession ?? "",
			goal: normalizeMappedValue(userDetails?.goal, GOAL_OPTIONS),
			familyNames: userDetails?.familyNames ?? "",
			children: normalizeMappedValue(userDetails?.children, CHILDREN_OPTIONS),
			country: editData?.country ?? "",
			city: editData?.city ?? "",
			freezeState: Boolean(editData?.freezeState),
			incoState: Boolean(editData?.incoState),
			minAge: parseIntSafe(userDetails?.minAge, 18),
			maxAge: parseIntSafe(userDetails?.maxAge, 80),
			height: parseIntSafe(userDetails?.height, 170),
			languages: userDetails?.languages ?? editData?.language ?? editData?.lang ?? "",
		});
	}, [isOpen, editData, userDetails]);

	const handleDeleteClick = () => {
		setShowPopup(true);
	};

	const handleConfirmDelete = async (
		deleteNoticeMessage = "",
		suppressNotification = true,
	) => {
		try {
			setIsDeleting(true);
			await deleteUser({
				userId: userData.userId,
				message: deleteNoticeMessage,
				suppressNotification,
			});

			setShowPopup(false);
			onConfirm();
		} finally {
			setIsDeleting(false);
		}
	};

	const handleInputChange = (event) => {
		const { name, value, type, checked } = event.target;

		setUserData((prev) => ({
			...prev,
			[name]:
				type === "radio"
					? Number(value)
					: type === "checkbox"
						? checked
						: value,
		}));
	};

	const handleMappedSelectChange = (name, nextValue) => {
		setUserData((prev) => ({
			...prev,
			[name]: nextValue === CUSTOM_OPTION ? "" : nextValue,
		}));
	};

	const handleSave = async () => {
		const payload = {
			Id: Number(userData.userId),
			UserId: Number(userData.userId),
			Name: userData.name?.trim() || "",
			SurName: userData.surName?.trim() || "",
			Gender: Number(userData.gender) || 0,
			Birthday: userData.birthday || null,
			Worldview: userData.worldview?.trim() || "",
			Description: userData.description?.trim() || "",
			Profession: userData.profession?.trim() || "",
			Goal: userData.goal?.trim() || "",
			FamilyNames: userData.familyNames?.trim() || "",
			Children: userData.children?.trim() || "",
			Country: userData.country?.trim() || "",
			City: userData.city?.trim() || "",
			FreezeState: Boolean(userData.freezeState),
			IncoState: Boolean(userData.incoState),
			MinAge: parseIntSafe(userData.minAge, 18),
			MaxAge: parseIntSafe(userData.maxAge, 80),
			Height: parseIntSafe(userData.height, 170),
			Languages: userData.languages?.trim() || "",
		};

		try {
			setIsSubmitting(true);
			await updateUser(payload);

			const savedUser = {
				userId: payload.UserId,
				name: payload.Name,
				surName: payload.SurName,
				fullName: [payload.Name, payload.SurName]
					.map((value) => String(value || "").trim())
					.filter(Boolean)
					.join(" "),
				gender: payload.Gender,
				birthday: payload.Birthday,
				worldview: payload.Worldview,
				description: payload.Description,
				profession: payload.Profession,
				goal: payload.Goal,
				familyNames: payload.FamilyNames,
				children: payload.Children,
				country: payload.Country,
				city: payload.City,
				freezeState: payload.FreezeState,
				incoState: payload.IncoState,
				minAge: payload.MinAge,
				maxAge: payload.MaxAge,
				height: payload.Height,
				languages: payload.Languages,
			};

			if (typeof onSaved === "function") {
				await Promise.resolve(onSaved(savedUser));
			}

			onClose();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleOverlayMouseDown = (event) => {
		if (event.target === event.currentTarget && !showPopup) {
			onClose();
		}
	};

	if (!isOpen) return null;

	const worldviewIsCustom = !isKnownValue(userData.worldview, WORLDVIEW_OPTIONS);
	const goalIsCustom = !isKnownValue(userData.goal, GOAL_OPTIONS);
	const childrenIsCustom = !isKnownValue(userData.children, CHILDREN_OPTIONS);
	const languageIsCustom = !isKnownValue(userData.languages, LANGUAGE_OPTIONS);
	const professionIsCustom = !isKnownValue(userData.profession, professionCodes.map((value) => ({ value })));

	return (
		<div className="popup-overlay" onMouseDown={handleOverlayMouseDown}>
			<ConfirmPopup
				isOpen={showPopup}
				onConfirm={handleConfirmDelete}
				onCancel={() => setShowPopup(false)}
				withMessageInput
				withSkipNotificationToggle
				title="Подтвердить удаление"
				skipNotificationLabel="Не отправлять уведомление"
				messageLabel="Причина для пользователя (необязательно)"
				messagePlaceholder="Например: доступ к приложению отклонен"
				isSubmitting={isDeleting}
			/>

			<div
				className="popup"
				role="dialog"
				aria-modal="true"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="popup-header">
					<h2>{t("profile_settings")}</h2>
					<button
						type="button"
						className="popup-close-button"
						onClick={onClose}
						aria-label={t("close")}
						disabled={isSubmitting}
					>
						x
					</button>
				</div>

				<div className="popup-body">
					<div className="popup-grid">
						<div className="popup-field">
							<label htmlFor="name">{t("name")}</label>
							<input
								id="name"
								type="text"
								name="name"
								value={userData.name}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="surName">{t("surname")}</label>
							<input
								id="surName"
								type="text"
								name="surName"
								value={userData.surName}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="birthday">{t("birthday")}</label>
							<input
								id="birthday"
								type="date"
								max={formatDate(
									new Date().setFullYear(new Date().getFullYear() - 18),
								)}
								name="birthday"
								value={userData.birthday}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="profession">{t("profession")}</label>
							<select
								id="profession"
								value={professionIsCustom ? CUSTOM_OPTION : userData.profession}
								onChange={(e) =>
									handleMappedSelectChange("profession", e.target.value)
								}
								disabled={isSubmitting}
							>
								<option value={CUSTOM_OPTION}>{t("reason_other")}</option>
								{professionCodes.map((code) => (
									<option key={code} value={code}>
										{t(code)}
									</option>
								))}
							</select>
							{professionIsCustom && (
								<input
									type="text"
									name="profession"
									value={userData.profession}
									onChange={handleInputChange}
									placeholder={t("fill_profession_other")}
									disabled={isSubmitting}
								/>
							)}
						</div>

						<div className="popup-field">
							<label htmlFor="worldview">{t("worldview")}</label>
							<select
								id="worldview"
								value={worldviewIsCustom ? CUSTOM_OPTION : userData.worldview}
								onChange={(e) =>
									handleMappedSelectChange("worldview", e.target.value)
								}
								disabled={isSubmitting}
							>
								<option value={CUSTOM_OPTION}>{t("reason_other")}</option>
								{WORLDVIEW_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{t(option.labelKey)}
									</option>
								))}
							</select>
							{worldviewIsCustom && (
								<input
									type="text"
									name="worldview"
									value={userData.worldview}
									onChange={handleInputChange}
									disabled={isSubmitting}
								/>
							)}
						</div>

						<div className="popup-field">
							<label htmlFor="goal">{t("here_for")}</label>
							<select
								id="goal"
								value={goalIsCustom ? CUSTOM_OPTION : userData.goal}
								onChange={(e) => handleMappedSelectChange("goal", e.target.value)}
								disabled={isSubmitting}
							>
								<option value={CUSTOM_OPTION}>{t("reason_other")}</option>
								{GOAL_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{t(option.labelKey)}
									</option>
								))}
							</select>
							{goalIsCustom && (
								<input
									type="text"
									name="goal"
									value={userData.goal}
									onChange={handleInputChange}
									disabled={isSubmitting}
								/>
							)}
						</div>

						<div className="popup-field">
							<label htmlFor="children">{t("children")}</label>
							<select
								id="children"
								value={childrenIsCustom ? CUSTOM_OPTION : userData.children}
								onChange={(e) =>
									handleMappedSelectChange("children", e.target.value)
								}
								disabled={isSubmitting}
							>
								<option value={CUSTOM_OPTION}>{t("reason_other")}</option>
								{CHILDREN_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{t(option.labelKey)}
									</option>
								))}
							</select>
							{childrenIsCustom && (
								<input
									type="text"
									name="children"
									value={userData.children}
									onChange={handleInputChange}
									disabled={isSubmitting}
								/>
							)}
						</div>

						<div className="popup-field">
							<label htmlFor="languages">{t("languages")}</label>
							<select
								id="languages"
								value={languageIsCustom ? CUSTOM_OPTION : userData.languages}
								onChange={(e) =>
									handleMappedSelectChange("languages", e.target.value)
								}
								disabled={isSubmitting}
							>
								<option value={CUSTOM_OPTION}>{t("reason_other")}</option>
								{LANGUAGE_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{t(option.labelKey)}
									</option>
								))}
							</select>
							{languageIsCustom && (
								<input
									type="text"
									name="languages"
									value={userData.languages}
									onChange={handleInputChange}
									placeholder="ru,en"
									disabled={isSubmitting}
								/>
							)}
						</div>

						<div className="popup-field">
							<label htmlFor="country">{t("country")}</label>
							<input
								id="country"
								type="text"
								name="country"
								value={userData.country}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="city">{t("city")}</label>
							<input
								id="city"
								type="text"
								name="city"
								value={userData.city}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="familyNames">{t("family_names")}</label>
							<input
								id="familyNames"
								type="text"
								name="familyNames"
								value={userData.familyNames}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="height">{t("height")}</label>
							<input
								id="height"
								type="number"
								name="height"
								value={userData.height}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="minAge">Min {t("age")}</label>
							<input
								id="minAge"
								type="number"
								name="minAge"
								value={userData.minAge}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field">
							<label htmlFor="maxAge">Max {t("age")}</label>
							<input
								id="maxAge"
								type="number"
								name="maxAge"
								value={userData.maxAge}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field popup-field--wide">
							<label htmlFor="description">{t("bio_about")}</label>
							<textarea
								id="description"
								className="textArea"
								name="description"
								value={userData.description}
								onChange={handleInputChange}
								maxLength={500}
								placeholder={t("max500")}
								disabled={isSubmitting}
							/>
						</div>

						<div className="popup-field popup-field--wide">
							<label className="popup-checkbox" htmlFor="freezeState">
								<input
									id="freezeState"
									type="checkbox"
									name="freezeState"
									checked={Boolean(userData.freezeState)}
									onChange={handleInputChange}
									disabled={isSubmitting}
								/>
								<span>{t("status")}: Freeze</span>
							</label>
						</div>

						<div className="popup-field popup-field--wide">
							<label className="popup-checkbox" htmlFor="incoState">
								<input
									id="incoState"
									type="checkbox"
									name="incoState"
									checked={Boolean(userData.incoState)}
									onChange={handleInputChange}
									disabled={isSubmitting}
								/>
								<span>{t("incognito_mode")}</span>
							</label>
						</div>
					</div>

					<div className="gender">
						<div
							className={`gender-option ${
								Number(userData.gender) === 1 ? "active male" : "male"
							}`}
						>
							<input
								id="male"
								type="radio"
								name="gender"
								value={1}
								checked={Number(userData.gender) === 1}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
							<label htmlFor="male">{t("man")}</label>
						</div>

						<div
							className={`gender-option ${
								Number(userData.gender) === 2 ? "active female" : "female"
							}`}
						>
							<input
								id="female"
								type="radio"
								name="gender"
								value={2}
								checked={Number(userData.gender) === 2}
								onChange={handleInputChange}
								disabled={isSubmitting}
							/>
							<label htmlFor="female">{t("woman")}</label>
						</div>
					</div>
				</div>

				<div className="popup-footer">
					<button
						type="button"
						onClick={handleDeleteClick}
						className="del-button"
						disabled={isSubmitting}
					>
						{t("delete")}
					</button>

					<div className="popup-buttons">
						<button
							type="button"
							onClick={onClose}
							className="cancel-button"
							disabled={isSubmitting}
						>
							{t("cancel")}
						</button>
						<button
							type="button"
							onClick={handleSave}
							className="save-button"
							disabled={isSubmitting}
						>
							{t("save")}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Popup;
